import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  tap,
  finalize,
  map,
  of,
  catchError,
} from 'rxjs';
import {
  AssignmentInputData,
  AssignmentStatus,
  AssignmentQuestion,
  DEFAULT_INPUT_DATA,
  DEFAULT_QUESTION_COUNT,
  STATUS_TO_MAX_STEP,
} from './assignment.models';
import {
  AssignmentApiService,
  AssignmentResponse,
} from './assignment-api.service';

@Injectable({
  providedIn: 'root',
})
export class AssignmentStateService {
  private readonly apiService = inject(AssignmentApiService);

  // ============================================
  // HELPERS
  // ============================================

  /**
   * Infer the wizard status based on assignment data.
   * Backend only stores 'draft' or 'published', so we determine
   * the wizard progress by checking what data exists.
   */
  inferStatusFromData(assignment: {
    status: AssignmentStatus;
    objective?: string;
    questions?: { id: string }[];
    dueDate?: string;
    timeLimit?: number;
  }): AssignmentStatus {
    // If published, return as-is
    if (assignment.status === AssignmentStatus.PUBLISHED) {
      return AssignmentStatus.PUBLISHED;
    }

    // For draft assignments, infer progress from data
    const hasQuestions = assignment.questions && assignment.questions.length > 0;
    const hasSettings = assignment.dueDate || assignment.timeLimit;
    const hasObjective =
      assignment.objective && assignment.objective.trim().length > 0;

    if (hasSettings) {
      return AssignmentStatus.SETTINGS_COMPLETED;
    }
    if (hasQuestions) {
      return AssignmentStatus.QUESTIONS_GENERATED;
    }
    if (hasObjective) {
      return AssignmentStatus.CONTEXT_COMPLETED;
    }

    return AssignmentStatus.DRAFT;
  }

  // ============================================
  // STATE
  // ============================================

  // Current assignment ID from backend
  private readonly assignmentId$$ = new BehaviorSubject<string | null>(null);
  readonly assignmentId$ = this.assignmentId$$.asObservable();

  // Assignment status from backend
  private readonly status$$ = new BehaviorSubject<AssignmentStatus>(
    AssignmentStatus.DRAFT,
  );
  readonly status$ = this.status$$.asObservable();

  // Max step the user can navigate to based on status
  readonly maxAllowedStep$ = this.status$.pipe(
    map((status) => STATUS_TO_MAX_STEP[status] ?? 1),
  );

  // Loading state for fetching assignment
  private readonly isLoading$$ = new BehaviorSubject<boolean>(false);
  readonly isLoading$ = this.isLoading$$.asObservable();

  // Current wizard step
  private readonly currentStep$$ = new BehaviorSubject<number>(1);
  readonly currentStep$ = this.currentStep$$.asObservable();

  // Input data (grade, subject, state, objective)
  private readonly inputData$$ =
    new BehaviorSubject<AssignmentInputData>(DEFAULT_INPUT_DATA);
  readonly inputData$ = this.inputData$$.asObservable();

  // File upload state
  private readonly uploadedFiles$$ = new BehaviorSubject<File[]>([]);
  readonly uploadedFiles$ = this.uploadedFiles$$.asObservable();

  private readonly isUploading$$ = new BehaviorSubject<boolean>(false);
  readonly isUploading$ = this.isUploading$$.asObservable();

  private readonly uploadProgress$$ = new BehaviorSubject<number>(0);
  readonly uploadProgress$ = this.uploadProgress$$.asObservable();

  // Loading state for AI generation
  private readonly isGenerating$$ = new BehaviorSubject<boolean>(false);
  readonly isGenerating$ = this.isGenerating$$.asObservable();

  // Generation progress step (0-3 for visual progress)
  private readonly generationProgressStep$$ = new BehaviorSubject<number>(0);
  readonly generationProgressStep$ =
    this.generationProgressStep$$.asObservable();

  // Auto-generated assignment title
  private readonly title$$ = new BehaviorSubject<string>('');
  readonly title$ = this.title$$.asObservable();

  // Generated questions
  private readonly questions$$ = new BehaviorSubject<AssignmentQuestion[]>([]);
  readonly questions$ = this.questions$$.asObservable();

  // Share code for published assignments
  private readonly shareCode$$ = new BehaviorSubject<string | null>(null);
  readonly shareCode$ = this.shareCode$$.asObservable();

  // Error state
  private readonly error$$ = new BehaviorSubject<string | null>(null);
  readonly error$ = this.error$$.asObservable();

  /**
   * Human-readable subject name resolved from tier2 subject.
   * Set by the step-context component when user selects a subject.
   * Sent to the backend as the `subject` field for prompt generation.
   */
  private _resolvedSubjectName: string | null = null;

  get resolvedSubjectName(): string | null {
    return this._resolvedSubjectName;
  }

  set resolvedSubjectName(name: string | null) {
    this._resolvedSubjectName = name;
  }

  // ============================================
  // GETTERS
  // ============================================

  get currentStep(): number {
    return this.currentStep$$.value;
  }

  get inputData(): AssignmentInputData {
    return this.inputData$$.value;
  }

  get assignmentId(): string | null {
    return this.assignmentId$$.value;
  }

  get status(): AssignmentStatus {
    return this.status$$.value;
  }

  get maxAllowedStep(): number {
    return STATUS_TO_MAX_STEP[this.status$$.value] ?? 1;
  }

  get isLoading(): boolean {
    return this.isLoading$$.value;
  }

  get uploadedFiles(): File[] {
    return this.uploadedFiles$$.value;
  }

  get title(): string {
    return this.title$$.value;
  }

  get questions(): AssignmentQuestion[] {
    return this.questions$$.value;
  }

  get shareCode(): string | null {
    return this.shareCode$$.value;
  }

  // ============================================
  // STATE SETTERS (used by sub-services)
  // ============================================

  setAssignmentId(id: string | null): void {
    this.assignmentId$$.next(id);
  }

  setStatus(status: AssignmentStatus): void {
    this.status$$.next(status);
  }

  setIsLoading(loading: boolean): void {
    this.isLoading$$.next(loading);
  }

  setIsUploading(uploading: boolean): void {
    this.isUploading$$.next(uploading);
  }

  setUploadProgress(progress: number): void {
    this.uploadProgress$$.next(progress);
  }

  setIsGenerating(generating: boolean): void {
    this.isGenerating$$.next(generating);
  }

  setGenerationProgressStep(step: number): void {
    this.generationProgressStep$$.next(step);
  }

  setTitle(title: string): void {
    this.title$$.next(title);
  }

  setQuestions(questions: AssignmentQuestion[]): void {
    this.questions$$.next(questions);
  }

  setShareCode(code: string | null): void {
    this.shareCode$$.next(code);
  }

  setError(error: string | null): void {
    this.error$$.next(error);
  }

  // ============================================
  // STEP NAVIGATION
  // ============================================

  /**
   * Set the current step, respecting the max allowed step based on status
   */
  setStep(step: number): void {
    const maxStep = this.maxAllowedStep;
    const clampedStep = Math.min(Math.max(1, step), maxStep);
    if (clampedStep !== this.currentStep$$.value) {
      this.currentStep$$.next(clampedStep);
    }
  }

  nextStep(): void {
    const maxStep = this.maxAllowedStep;
    if (this.currentStep$$.value < maxStep) {
      const newStep = this.currentStep$$.value + 1;
      this.currentStep$$.next(newStep);
    }
  }

  prevStep(): void {
    if (this.currentStep$$.value > 1) {
      const newStep = this.currentStep$$.value - 1;
      this.currentStep$$.next(newStep);
    }
  }

  /**
   * Check if user can navigate to a specific step
   */
  canNavigateToStep(step: number): boolean {
    return step >= 1 && step <= this.maxAllowedStep;
  }

  // ============================================
  // LOAD EXISTING ASSIGNMENT
  // ============================================

  /**
   * Load an existing assignment by ID and populate the service state
   */
  loadAssignment(id: string): Observable<AssignmentResponse | null> {
    this.isLoading$$.next(true);
    this.error$$.next(null);

    return this.apiService.getAssignment(id).pipe(
      tap((assignment) => {
        // Infer wizard status from assignment data
        const inferredStatus = this.inferStatusFromData(assignment);

        // Populate all state from the loaded assignment
        this.assignmentId$$.next(assignment.id);
        this.status$$.next(inferredStatus);
        this.inputData$$.next({
          grade: parseInt(assignment.grade, 10),
          tier1SubjectId: assignment.tier1SubjectId ?? null,
          tier2SubjectId: assignment.tier2SubjectId ?? null,
          state: assignment.state,
          objective: assignment.objective || '',
          questionCount:
            (assignment as any).questionCount ?? DEFAULT_QUESTION_COUNT,
        });
        this.title$$.next(assignment.title || '');
        this.questions$$.next(assignment.questions || []);
        this.shareCode$$.next(assignment.shareCode || null);

        // Set current step to max allowed based on inferred status
        const maxStep = STATUS_TO_MAX_STEP[inferredStatus];
        this.currentStep$$.next(maxStep);
      }),
      catchError(() => {
        this.error$$.next(
          'Failed to load assignment. It may not exist or you may not have access.',
        );
        return of(null);
      }),
      finalize(() => this.isLoading$$.next(false)),
    );
  }

  // ============================================
  // INPUT DATA
  // ============================================

  updateInputData(data: Partial<AssignmentInputData>): void {
    this.inputData$$.next({ ...this.inputData$$.value, ...data });
  }

  // ============================================
  // FILE UPLOAD
  // ============================================

  addFiles(files: File[]): void {
    const currentFiles = this.uploadedFiles$$.value;
    // Filter out duplicates by name
    const newFiles = files.filter(
      (file) =>
        !currentFiles.some(
          (f) => f.name === file.name && f.size === file.size,
        ),
    );
    this.uploadedFiles$$.next([...currentFiles, ...newFiles]);
  }

  removeFile(file: File): void {
    const files = this.uploadedFiles$$.value.filter((f) => f !== file);
    this.uploadedFiles$$.next(files);
  }

  clearFiles(): void {
    this.uploadedFiles$$.next([]);
  }

  // ============================================
  // RESET
  // ============================================

  /**
   * Reset all state to initial values
   */
  reset(): void {
    this.currentStep$$.next(1);
    this.assignmentId$$.next(null);
    this.status$$.next(AssignmentStatus.DRAFT);
    this.inputData$$.next(DEFAULT_INPUT_DATA);
    this.uploadedFiles$$.next([]);
    this.uploadProgress$$.next(0);
    this.isUploading$$.next(false);
    this.isLoading$$.next(false);
    this.isGenerating$$.next(false);
    this.generationProgressStep$$.next(0);
    this.title$$.next('');
    this.questions$$.next([]);
    this.shareCode$$.next(null);
    this.error$$.next(null);
    this._resolvedSubjectName = null;
  }
}
